import React, { useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { HPFAQSection } from '@/components/sections/HPFAQSection';
import { ClaimForm } from '@/forms/ClaimForm';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, AlertCircle, FileText, Scale, CheckCircle, ArrowRight, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';

const HPClaimsPage = () => {
  // Handle scroll behavior on page load
  useEffect(() => {
    if (window.location.hash) {
      // If there's a hash, scroll to that element after a short delay
      setTimeout(() => {
        const element = document.getElementById(window.location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const hpQuestions = [
    {
      id: 'took_out_hp',
      question: 'Did you take out HP (Hire Purchase) car finance?',
      options: [
        { label: 'Yes, I had HP finance', value: 'hp' },
        { label: 'Yes, I had PCP finance', value: 'pcp' },
        { label: 'Yes, I had both HP and PCP at different times', value: 'both' },
        { label: "I'm not sure what type of finance I had", value: 'unsure' }
      ]
    },
    {
      id: 'commission_disclosed',
      question: 'Were you told that the dealer received commission from the lender?',
      options: [
        { label: 'Yes, commission was disclosed to me', value: 'yes' },
        { label: 'No, I was not told about any commission', value: 'no' },
        { label: "I don't remember being told", value: 'unsure' }
      ]
    },
    {
      id: 'finance_date',
      question: 'When did you take out the car finance?',
      options: [
        { label: 'Between April 2007 and January 2021', value: 'eligible_period' },
        { label: 'After January 2021', value: 'after_ban' },
        { label: 'Before April 2007', value: 'before_period' },
        { label: "I'm not sure of the exact date", value: 'unsure' }
      ]
    },
    {
      id: 'interest_rate_explained',
      question: 'Was the interest rate on your finance fully explained to you?',
      options: [
        { label: 'Yes, it was clearly explained', value: 'yes' },
        { label: "No, I didn't fully understand the interest rate", value: 'no' },
        { label: "I'm not sure", value: 'unsure' }
      ]
    }
  ];

  const hpFAQQuestions = [
    {
      question: "What is HP (Hire Purchase) finance?",
      answer: "Hire Purchase is a type of car finance where you pay a deposit followed by fixed monthly payments. Unlike PCP, you're paying off the full value of the car, and you automatically own it once all payments are made."
    },
    {
      question: "How could I have been mis-sold HP finance?",
      answer: "If you weren't told about commission the dealer received from the lender, if the finance wasn't suitable for your circumstances, or if important terms weren't properly explained, your HP agreement may have been mis-sold."
    },
    {
      question: "What compensation could I receive?",
      answer: "The FCA estimates average compensation of around £830 per agreement, though this varies based on factors including the commission paid, the loan amount, and the interest rate charged."
    },
    {
      question: "Can I claim if I've finished paying?",
      answer: "Yes, you can claim for HP agreements that have been fully paid off. We can find agreements going back to 2007, even if you no longer have any paperwork."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary relative overflow-hidden">
          <div className="absolute inset-0 hero-pattern opacity-50" />
          <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 rounded-full bg-accent/10 blur-3xl" />
          
          <div className="section-container relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">SRA Regulated Law Firm</span>
              </div>
              
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 leading-tight">
                HP Car Finance Claims
              </h1>
              
              <p className="text-lg sm:text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                Were you mis-sold Hire Purchase car finance? Hidden commission may mean you're owed 
                significant compensation. Find out in minutes with our eligibility check.
              </p>
              
              <Button variant="gold" size="xl" asChild>
                <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                  Find Your Agreements
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Info Section */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                  Understanding HP Finance Mis-selling
                </h2>
                <div className="prose prose-lg max-w-none">
                  <p className="text-foreground/80 leading-relaxed mb-4">
                    Hire Purchase (HP) is one of the most common ways to finance a car in the UK. 
                    Like PCP agreements, many HP contracts included hidden commission arrangements 
                    where dealers received payments from lenders without telling you.
                  </p>
                  <p className="text-foreground/80 leading-relaxed mb-4">
                    The FCA has found that these undisclosed commission payments often led to higher 
                    interest rates and inflated costs for consumers. If you took out HP finance 
                    between 2007 and today, you may be entitled to claim compensation.
                  </p>
                  <p className="text-foreground/80 leading-relaxed">
                    Blue Lion Claims can find all your HP agreements going back to 2007 — even if 
                    you've moved, changed your name, or have no paperwork. All we need is your 
                    name, date of birth, and current address.
                  </p>
                </div>
              </div>
              
              <Card className="border-border shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-destructive" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-foreground">
                      Signs of HP Mis-selling
                    </h3>
                  </div>
                  <ul className="space-y-4">
                    {[
                      "You weren't told about dealer commission",
                      "The interest rate seemed higher than expected",
                      "You felt pressured into the finance option",
                      "Alternative finance options weren't discussed",
                      "The total cost wasn't clearly explained"
                    ].map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                        <span className="text-foreground/80">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 lg:py-24 bg-muted/30">
          <div className="section-container">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground text-center mb-12">
              HP Claims: Common Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {hpFAQQuestions.map((item, index) => (
                <Card key={index} className="border-border">
                  <CardContent className="p-6">
                    <h3 className="font-heading font-semibold text-foreground mb-3">
                      {item.question}
                    </h3>
                    <p className="text-foreground/70 text-sm leading-relaxed">
                      {item.answer}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Claim Form Section */}
        <section id="claim-form" className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-4">
                  Start Your HP Claim
                </h2>
                <p className="text-foreground/70">
                  Enter your details below and we'll find all your HP agreements going back to 2007.
                </p>
              </div>
              <ClaimForm claimType="hp" questions={hpQuestions} />
            </div>
          </div>
        </section>

        {/* HP Finance FAQ Section */}
        <HPFAQSection />
      </main>
      <Footer />
    </div>
  );
};

export default HPClaimsPage;
